import { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  FiCheck, FiX, FiMapPin, FiUsers, FiCalendar, FiAlertTriangle,
  FiRefreshCw, FiCheckCircle, FiAlertCircle, FiTrash2, FiDollarSign, FiSearch,
} from 'react-icons/fi';
import {
  getSuperAdminDerasars, approveDerasar, renewDerasar, deleteDerasar, markOfflinePaid,
} from '../../services/api';
import styles from './SuperAdminPage.module.css';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', {
  day: '2-digit',
  month: 'short',
  year: 'numeric',
  timeZone: 'Asia/Kolkata',
}) : '—';

const getSubBadge = (sub) => {
  const map = { active: styles.subActive, expired: styles.subExpired, pending: styles.subPending, pending_renewal: styles.subRenewal };
  const labels = { active: 'Active', expired: 'Expired', pending: 'Pending', pending_renewal: 'Renewal Req.' };
  return <span className={map[sub] || styles.subPending}>{labels[sub] || sub}</span>;
};

const getDaysUntilExpiry = (endDate) => {
  if (!endDate) return null;
  return Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24));
};

const TABS = [
  { id: 'pending', label: 'Pending Approval' },
  { id: 'approved', label: 'Approved' },
  { id: 'renewal', label: 'Renewal Payments' },
  { id: 'expired', label: 'Expired' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'all', label: 'All' },
];

// -- Main Component -------------------------------------------------------------
const SuperAdminPage = () => {
  const [derasars, setDerasars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const fetchDerasars = async () => {
    setLoading(true);
    try {
      let params = {};
      if (tab === 'pending') params = { status: 'pending' };
      else if (tab === 'approved') params = { status: 'approved', subscriptionStatus: 'active' };
      else if (tab === 'renewal') params = { subscriptionStatus: 'pending_renewal' };
      else if (tab === 'expired') params = { subscriptionStatus: 'expired' };
      else if (tab === 'rejected') params = { status: 'rejected' };
      const res = await getSuperAdminDerasars(params);
      setDerasars(res.data);
    } catch {
      toast.error('Failed to load derasars');
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchDerasars(); }, [tab]);

  const handleApprove = async (id, subscriptionYears) => {
    setActionLoading(true);
    try {
      await approveDerasar(id, { action: 'approve' });
      toast.success(`Derasar approved — subscription valid for ${subscriptionYears || 1} year(s)`);
      setDetail(null);
      fetchDerasars();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to approve');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectModal) return;
    setActionLoading(true);
    try {
      await approveDerasar(rejectModal.id, { action: 'reject', rejectionReason });
      toast.success('Derasar rejected');
      setRejectModal(null);
      setRejectionReason('');
      setDetail(null);
      fetchDerasars();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to reject');
    } finally {
      setActionLoading(false);
    }
  };

  const handleMarkOfflinePaid = async (id) => {
    setActionLoading(true);
    try {
      await markOfflinePaid(id);
      toast.success('Offline payment marked as paid — you can now approve the derasar');
      setDetail(null);
      fetchDerasars();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to mark as paid');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRenew = async (id, name, years) => {
    setActionLoading(true);
    try {
      await renewDerasar(id, years);
      toast.success(`${name} renewed`);
      setDetail(null);
      fetchDerasars();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to renew');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Permanently delete "${name}" and its admin account? This cannot be undone.`)) return;
    setActionLoading(true);
    try {
      await deleteDerasar(id);
      toast.success(`${name} deleted`);
      setDetail(null);
      fetchDerasars();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    } finally {
      setActionLoading(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filtered = q
    ? derasars.filter((d) => [d.name, d.city].filter(Boolean).some((s) => String(s).toLowerCase().includes(q)))
    : derasars;

  const renderActions = (d) => {
    const days = getDaysUntilExpiry(d.endDate);
    const expiringSoon = days !== null && days >= 0 && days <= 30;
    return (
      <>
        {d.status === 'pending' && (
          <>
            <button
              className="btn btn-success btn-sm"
              onClick={() => handleApprove(d.id, d.subscriptionYears)}
              disabled={actionLoading || !d.isPaymentVerified}
              title={!d.isPaymentVerified ? 'Payment not verified — cannot approve' : ''}
            >
              <FiCheck size={13} /> Approve
            </button>
            <button className="btn btn-danger btn-sm" onClick={() => setRejectModal({ id: d.id, name: d.name })} disabled={actionLoading}>
              <FiX size={13} /> Reject
            </button>
          </>
        )}
        {!!d.renewalRequestedAt && (
          <button className="btn btn-primary btn-sm" onClick={() => handleRenew(d.id, d.name, d.subscriptionYears)} disabled={actionLoading}>
            <FiRefreshCw size={13} /> Renew {d.subscriptionYears > 1 ? `${d.subscriptionYears} Yrs` : '1 Yr'}
          </button>
        )}
        {expiringSoon && d.subscriptionStatus === 'active' && (
          <button className="btn btn-ghost btn-sm" onClick={() => handleRenew(d.id, d.name, d.subscriptionYears)} disabled={actionLoading}>
            <FiRefreshCw size={13} /> Extend Now
          </button>
        )}
        {d.status === 'rejected' && (
          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(d.id, d.name)} disabled={actionLoading}>
            <FiTrash2 size={13} /> Delete
          </button>
        )}
        {d.paymentMode === 'offline' && !d.isPaymentVerified && (
          <button className={`btn btn-sm ${styles.markPaidBtn}`} onClick={() => handleMarkOfflinePaid(d.id)} disabled={actionLoading} title="Mark offline payment as received">
            <FiDollarSign size={13} style={{ marginRight: 4 }} /> Mark as Paid
          </button>
        )}
      </>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h2>Derasar Management</h2>
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginTop: 4 }}>
          Approve registrations and manage subscriptions
        </p>
      </div>

      {/* Tab navigation */}
      <div className={styles.tabNav}>
        {TABS.map((t) => (
          <button
            key={t.id}
            className={`${styles.tabBtn} ${tab === t.id ? styles.activeTab : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className={styles.toolbar}>
        <div className={styles.searchBox}>
          <FiSearch size={15} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by derasar name or city…"
          />
          {search && <button className={styles.searchClear} onClick={() => setSearch('')} aria-label="Clear search"><FiX size={14} /></button>}
        </div>
        {!loading && <span className={styles.resultCount}>{filtered.length} derasar{filtered.length !== 1 ? 's' : ''}</span>}
      </div>

      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <p>{q ? 'No derasars match your search' : 'No derasars found'}</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Derasar</th>
                  <th>Status</th>
                  <th>Subscription</th>
                  <th>Expires</th>
                  <th>Admin</th>
                  <th className={styles.actionsCol}></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((d) => {
                  const days = getDaysUntilExpiry(d.endDate);
                  const expiringSoon = days !== null && days >= 0 && days <= 30;
                  const isExpired = days !== null && days < 0;
                  return (
                    <tr key={d.id} className={styles.rowClickable} onClick={() => setDetail(d)}>
                      <td>
                        <div className={styles.cellName}>{d.name}</div>
                        <div className={styles.cellSub}><FiMapPin size={11} /> {d.city || '—'}</div>
                      </td>
                      <td><span className={`badge badge-${d.status}`}>{d.status}</span></td>
                      <td>
                        {getSubBadge(d.subscriptionStatus)}
                        {d.subscriptionYears ? <span className={styles.cellSub}> · {d.subscriptionYears}yr</span> : null}
                      </td>
                      <td style={{ color: isExpired ? 'var(--color-danger)' : expiringSoon ? '#b45309' : undefined, whiteSpace: 'nowrap' }}>
                        {d.endDate ? formatDate(d.endDate) : '—'}
                      </td>
                      <td>
                        <div className={styles.cellName}>{d.admin?.name || '—'}</div>
                        <div className={styles.cellSub}>{d.admin?.email}</div>
                      </td>
                      <td className={styles.actionsCol} onClick={(e) => e.stopPropagation()}>
                        <button className="btn btn-secondary btn-sm" onClick={() => setDetail(d)}>View</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detail && (() => {
        const d = detail;
        const days = getDaysUntilExpiry(d.endDate);
        const expiringSoon = days !== null && days >= 0 && days <= 30;
        const isExpired = days !== null && days < 0;
        return (
          <div className="modal-overlay" onClick={() => setDetail(null)}>
            <div className={styles.detailModal} onClick={(e) => e.stopPropagation()}>
              <div className={styles.detailHeader}>
                <div>
                  <div className={styles.detailTitle}>{d.name}</div>
                  <div className={styles.detailLoc}><FiMapPin size={12} /> {d.city || '—'}</div>
                </div>
                <div className={styles.detailBadges}>
                  <span className={`badge badge-${d.status}`}>{d.status}</span>
                  {d.paymentMode === 'offline' && (
                    <span className={styles.offlineBadge}><FiDollarSign size={11} /> Offline Payment</span>
                  )}
                  <button className={styles.detailClose} onClick={() => setDetail(null)} aria-label="Close"><FiX size={18} /></button>
                </div>
              </div>

              <div className={styles.detailBody}>
                {d.description && <div className={styles.description} style={{ marginBottom: 16 }}>{d.description}</div>}

                <div className={styles.adminInfo}>
                  <div className={styles.adminLabel}>Subscription &amp; Details</div>
                  <div className={styles.infoGrid}>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}><FiUsers size={11} /> Members</span>
                    <span className={styles.infoValue}>{d.memberCount ?? d.members?.length ?? 0}</span>
                  </div>
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Subscription</span>
                    <span className={styles.infoValue}>{getSubBadge(d.subscriptionStatus)}</span>
                  </div>
                  {d.subscriptionYears && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}>Paid Duration</span>
                      <span className={styles.infoValue}>{d.subscriptionYears} year{d.subscriptionYears > 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {d.startDate && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}><FiCalendar size={11} /> Start Date</span>
                      <span className={styles.infoValue}>{formatDate(d.startDate)}</span>
                    </div>
                  )}
                  {d.registeredSince && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}><FiCalendar size={11} /> Registered Since</span>
                      <span className={styles.infoValue}>{formatDate(d.registeredSince)}</span>
                    </div>
                  )}
                  {d.endDate && (
                    <div className={styles.infoItem}>
                      <span className={styles.infoLabel}><FiCalendar size={11} /> Expires On</span>
                      <span className={styles.infoValue} style={{ color: isExpired ? 'var(--color-danger)' : expiringSoon ? '#b45309' : undefined }}>{formatDate(d.endDate)}</span>
                    </div>
                  )}
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Registered</span>
                    <span className={styles.infoValue}>{formatDate(d.createdAt)}</span>
                  </div>
                  </div>
                </div>

                {expiringSoon && (
                  <div className={styles.expiryWarning}><FiAlertTriangle size={13} /> Expires in {days} day{days !== 1 ? 's' : ''}</div>
                )}
                {isExpired && (
                  <div className={styles.rejectionNote}>Subscription expired {Math.abs(days)} day{Math.abs(days) !== 1 ? 's' : ''} ago</div>
                )}
                {d.renewalRequestedAt && (
                  <div className={styles.renewalNote}><FiRefreshCw size={11} style={{ marginRight: 4 }} /> Renewal requested on {formatDate(d.renewalRequestedAt)}</div>
                )}

                <div className={styles.adminInfo}>
                  <div className={styles.adminLabel}>Subscription Payment</div>
                  <div className={styles.contactGrid}>
                    <div className={styles.contactRow}>
                      <span className={styles.contactKey}>Mode</span>
                      <span className={styles.contactVal}>
                        {d.paymentMode === 'offline' ? 'Offline' : d.paymentMode ? 'Online' : '—'}
                      </span>
                    </div>
                    {d.paymentMode && d.paymentMode !== 'offline' && (
                      <div className={styles.contactRow}>
                        <span className={styles.contactKey}>Payment ID</span>
                        <span className={styles.contactVal} style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{d.razorpayPaymentId || '—'}</span>
                      </div>
                    )}
                    <div className={styles.contactRow}>
                      <span className={styles.contactKey}>Status</span>
                      <span className={styles.contactVal}>
                        {d.isPaymentVerified
                          ? <span className={styles.verifiedTag}><FiCheckCircle size={12} /> Payment verified</span>
                          : <span className={styles.unverifiedTag}><FiAlertCircle size={12} /> Not verified{d.status === 'pending' ? ' — cannot approve' : ''}</span>}
                      </span>
                    </div>
                  </div>
                </div>

                <div className={styles.adminInfo}>
                  <div className={styles.adminLabel}>Admin Contact</div>
                  <div className={styles.contactGrid}>
                    <div className={styles.contactRow}>
                      <span className={styles.contactKey}>Name</span>
                      <span className={styles.contactVal}>{d.admin?.name || '—'}</span>
                    </div>
                    <div className={styles.contactRow}>
                      <span className={styles.contactKey}>Email</span>
                      <span className={styles.contactVal}>
                        {d.admin?.email || '—'}
                        {d.admin?.email && (d.admin?.isEmailVerified
                          ? <span className={styles.verifiedTag}><FiCheckCircle size={12} /> Verified</span>
                          : <span className={styles.unverifiedTag}><FiAlertCircle size={12} /> Unverified</span>)}
                      </span>
                    </div>
                    <div className={styles.contactRow}>
                      <span className={styles.contactKey}>Mobile</span>
                      <span className={styles.contactVal}>
                        {d.admin?.phone || '—'}
                        {d.admin?.phone && (d.admin?.isMobileVerified
                          ? <span className={styles.verifiedTag}><FiCheckCircle size={12} /> Verified</span>
                          : <span className={styles.unverifiedTag}><FiAlertCircle size={12} /> Unverified</span>)}
                      </span>
                    </div>
                  </div>
                </div>

                {d.rejectionReason && (
                  <div className={styles.rejectionNote}>Rejection reason: {d.rejectionReason}</div>
                )}
              </div>

              <div className={styles.detailFooter}>
                {renderActions(d)}
                <button className="btn btn-ghost btn-sm" onClick={() => setDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Reject modal */}
      {rejectModal && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>Reject "{rejectModal.name}"</h3>
            <div className="form-group" style={{ marginTop: 16 }}>
              <label>Rejection Reason</label>
              <textarea
                rows={3}
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Optional: provide a reason"
              />
            </div>
            <div className="modal-actions">
              <button className="btn btn-ghost" onClick={() => setRejectModal(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={handleReject} disabled={actionLoading}>
                Confirm Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminPage;
